/**
 * Banner konstruktori — dizayn shartnomasi (admin panel tomoni).
 * App tomonidagi `app/src/lib/bannerDesign.ts` bilan bir xil bo'lishi shart —
 * jonli preview ilovadagi ko'rinishga to'liq mos kelishi uchun.
 */

export type BannerTemplate = 'HERO' | 'SOLID' | 'GRADIENT' | 'SPLIT' | 'MINIMAL' | 'TICKET';
export const TEMPLATES: BannerTemplate[] = ['HERO', 'SOLID', 'GRADIENT', 'SPLIT', 'MINIMAL', 'TICKET'];
export type Lang = 'uz' | 'ru' | 'en';
export const LANGS: Lang[] = ['uz', 'ru', 'en'];

export interface BannerLang { title?: string; body?: string; cta?: string; eyebrow?: string }
export interface BannerContent { uz?: BannerLang; ru?: BannerLang; en?: BannerLang }

export interface BannerTheme {
  useGradient?: boolean;
  bg?: string;
  grad1?: string;
  grad2?: string;
  text?: 'light' | 'dark';
  accent?: string;
  imageKey?: string;
}

export interface BannerDesign {
  align?: 'left' | 'center';
  showCta?: boolean;
  emoji?: string;
  scrim?: boolean; // HERO: rasm ustidagi qora gradient (matn o'qilishi uchun)
  light?: BannerTheme;
  dark?: BannerTheme;
}

export function pickLang(content: BannerContent | null | undefined, lang: string): BannerLang {
  const c = content || {};
  const base = c.uz || {};
  const cur = (c as Record<string, BannerLang>)[lang] || {};
  return {
    title: cur.title || base.title,
    body: cur.body || base.body,
    cta: cur.cta || base.cta,
    eyebrow: cur.eyebrow || base.eyebrow,
  };
}

/**
 * Shablonlarning STANDART (design saqlanmagan) qiymatlari.
 *
 * ── KELISHILGAN BANNER STANDARTLARI (front va app da AYNAN BIR XIL) ─────────
 * Tanlov mezonlari: (1) asos — brendning MONOXROM ko'ki (app/src/theme.ts:
 * brand #0068F8 · brandDark #0057D0 · chuqur ko'k #0F4FB8 · dark brand #4C93FF);
 * (2) boshqa rang FAQAT shablonning MA'NOSI talab qilganda; (3) har bir standart
 * WCAG AA dan o'tadi. Eyebrow kichik o'lchamda chiqadi (oddiy matn), shuning
 * uchun barcha juftliklar >= 4.5:1 qilib olindi — katta matn uchun 3:1 chegara
 * bilan cheklanmadik.
 *
 *  Shablon  | LIGHT fon              | matn | aksent  | DARK fon | aksent  | eng yomon juftlik
 *  ---------+------------------------+------+---------+----------+---------+---------------------------------
 *  HERO     | grad #0068F8 → #0F4FB8 | oq   | #FFFFFF | bir xil  | #FFFFFF | oq/#0068F8 4.84
 *  SOLID    | #0068F8                | oq   | #FFFFFF | #0057D0  | #FFFFFF | oq/#0068F8 4.84
 *  GRADIENT | grad #0057D0 → #0F4FB8 | oq   | #FFFFFF | bir xil  | #FFFFFF | oq/#0057D0 6.41
 *  SPLIT    | oq karta (fonsiz)      | to'q | #0068F8 | surface  | #4C93FF | #0068F8/oq 4.84 · #4C93FF/#141D2E 5.56
 *  MINIMAL  | oq karta (fonsiz)      | to'q | #D12943 | surface  | #FF6376 | #D12943/oq 5.11 · #FF6376/#141D2E 5.86
 *  TICKET   | #101E36 (siyoh)        | oq   | #EBA210 | #0C1729  | #F5B62B | oltin/siyoh 7.70 · oq/siyoh 16.65
 *
 * MA'NO uchun brend ko'kidan chetga chiqqan IKKI shablon:
 *  • MINIMAL — "diqqat" e'loni: semantik qizil (theme `coral` oilasi). Aynan
 *    `coral` #F2445B EMAS — u oq fonda 3.65:1, ya'ni AA dan o'tmaydi; bir
 *    pog'ona to'q ohang #D12943 olindi (5.11:1).
 *  • TICKET — chipta/sovg'a: siyoh fon + premium OLTIN muhr. Ko'k variant
 *    "chipta" ma'nosini yo'qotardi, shuning uchun oltin saqlandi.
 * Tuzatilgan AA xatolari: GRADIENT app'da #12B5A5 → #0068F8 (oq matn 2.57:1),
 * front'da #3D8BFF → #0057D0 (3.31:1) edi; HERO app'da #3D8BFF dan boshlanardi
 * (3.31:1). Endi gradientning ENG OCH nuqtasi ham AA dan o'tadi.
 *
 * `emoji` standarti BO'SH: loyiha qoidasi bo'yicha UI'da emoji-stiker
 * ishlatilmaydi (admin xohlasa konstruktordan o'zi kiritadi).
 * MUHIM: bular faqat STANDART qiymatlar — saqlangan `design` i bor bannerlar
 * `mergeDesign` da o'z qiymatlarini ustun qo'yadi va o'zgarmaydi.
 */
export function templateDefaults(tpl: BannerTemplate): BannerDesign {
  switch (tpl) {
    case 'HERO':
      return { align: 'left', showCta: true, emoji: '', scrim: true,
        light: { useGradient: true, grad1: '#0068F8', grad2: '#0F4FB8', text: 'light', accent: '#FFFFFF' },
        dark: { useGradient: true, grad1: '#0068F8', grad2: '#0F4FB8', text: 'light', accent: '#FFFFFF' } };
    case 'SOLID':
      return { align: 'left', showCta: false, emoji: '',
        light: { bg: '#0068F8', text: 'light', accent: '#FFFFFF' },
        dark: { bg: '#0057D0', text: 'light', accent: '#FFFFFF' } };
    case 'GRADIENT':
      // HERO dan bir pog'ona TO'Q juftlik: ikkalasi ham brend ko'ki, lekin
      // "bayram" gradienti chuqurroq va oq bezak doiralari unda yaxshi ko'rinadi.
      return { align: 'left', showCta: false, emoji: '',
        light: { useGradient: true, grad1: '#0057D0', grad2: '#0F4FB8', text: 'light', accent: '#FFFFFF' },
        dark: { useGradient: true, grad1: '#0057D0', grad2: '#0F4FB8', text: 'light', accent: '#FFFFFF' } };
    case 'SPLIT':
      return { align: 'left', showCta: false, emoji: '',
        light: { accent: '#0068F8', text: 'dark' },
        dark: { accent: '#4C93FF', text: 'dark' } };
    case 'MINIMAL':
      return { align: 'left', showCta: false, emoji: '',
        light: { accent: '#D12943', text: 'dark' },
        dark: { accent: '#FF6376', text: 'dark' } };
    case 'TICKET':
      return { align: 'left', showCta: false, emoji: '',
        light: { bg: '#101E36', text: 'light', accent: '#EBA210' },
        dark: { bg: '#0C1729', text: 'light', accent: '#F5B62B' } };
  }
}

export function mergeDesign(tpl: BannerTemplate, design?: BannerDesign | null) {
  const d = design || {};
  const def = templateDefaults(tpl);
  return {
    align: d.align ?? def.align ?? 'left',
    showCta: d.showCta ?? def.showCta ?? false,
    emoji: d.emoji ?? def.emoji ?? '',
    scrim: d.scrim ?? def.scrim ?? true,
    light: { ...def.light, ...(d.light || {}) } as BannerTheme,
    dark: { ...def.dark, ...(d.dark || {}) } as BannerTheme,
  };
}

/** App tema tokenlari (preview ilovadagi ko'rinishga mos bo'lishi uchun).
 *  Qiymatlar app/src/theme.ts dagi `colors` / `darkColors` dan aynan ko'chirilgan —
 *  ilgari bu yerda eski binafsha palitra qolib ketgan edi va preview ilovadagi
 *  haqiqiy ko'rinishdan farq qilardi. */
export const APP_TOKENS = {
  light: { bg: '#F5F7FC', surface: '#FFFFFF', border: '#E9EEF6', text: '#0F1B33', textDim: '#78849B', brand: '#0068F8', coral: '#F2445B' },
  dark: { bg: '#0B1220', surface: '#141D2E', border: '#222E44', text: '#EEF3FB', textDim: '#8C9AB3', brand: '#4C93FF', coral: '#FF6376' },
};

/** Konstruktor UI uchun shablon metama'lumoti. */
export interface TemplateMeta {
  label: string;
  desc: string;
  contentFields: (keyof BannerLang)[];
  ctrl: { gradient?: boolean; solidBg?: boolean; image?: boolean; emoji?: boolean; accent?: boolean; textTone?: boolean; align?: boolean; ticketBg?: boolean };
}

/** Shablon nomi/tavsifi — tilga bog'liq (ko'rsatish uchun). TEMPLATE_META.label/desc — faqat
 *  barqaror zaxira (masalan saqlanadigan default sarlavha). */
export const templateLabel = (t: (k: string) => string, tpl: BannerTemplate) => t(`bannerTemplate.${tpl}.label`);
export const templateDesc = (t: (k: string) => string, tpl: BannerTemplate) => t(`bannerTemplate.${tpl}.desc`);

export const TEMPLATE_META: Record<BannerTemplate, TemplateMeta> = {
  HERO: { label: 'Rasmli hero', desc: "To'liq rasm/gradient + scrim + CTA", contentFields: ['title', 'body', 'cta'], ctrl: { image: true, gradient: true, accent: true, align: true } },
  SOLID: { label: "To'liq rang", desc: 'Rang/gradient + ikonka + matn', contentFields: ['title', 'body'], ctrl: { solidBg: true, gradient: true, emoji: true, textTone: true, accent: true } },
  GRADIENT: { label: 'Gradient bayram', desc: 'Gradient + bezak doiralar', contentFields: ['title', 'body', 'cta'], ctrl: { gradient: true, emoji: true, textTone: true, align: true } },
  SPLIT: { label: 'Split (media + matn)', desc: 'Chapda media, o‘ngda matn', contentFields: ['title', 'body', 'cta'], ctrl: { image: true, emoji: true, accent: true } },
  MINIMAL: { label: 'Minimal e‘lon', desc: 'Yengil karta + rangli chiziq', contentFields: ['eyebrow', 'title', 'body'], ctrl: { accent: true } },
  TICKET: { label: 'Chipta (premium)', desc: 'Chipta uslubi + muhr', contentFields: ['title', 'body'], ctrl: { ticketBg: true, emoji: true, accent: true } },
};
